import { useState } from 'react';
import { Pressable, Text, StyleSheet } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import Toast from 'react-native-toast-message';
import { ADMIN_COLORS, ADMIN_FONT } from '../../config/theme/adminColors';
import CoverAiReferencePickerModal from '../Modals/CoverAiReferencePickerModal';
import {
  buildCoverAiCopyToastMessage,
  copyCoverAiAssistantToClipboard,
} from '../../utils/aiAssistantReferenceImages';

function normalizePromptResult(result) {
  if (typeof result === 'string') {
    return { text: result, attachmentUrls: [] };
  }

  return {
    text: result?.text || '',
    attachmentUrls: result?.attachmentUrls || [],
  };
}

export default function CoverImageAiAssistantButton({
  label = 'Gerar com IA',
  onBuildPrompt,
  getReferenceOptions,
  buildPrompt,
  pickerTitle,
  pickerSubtitle,
  variant = 'default',
  disabled = false,
  onFeedback,
}) {
  const [loading, setLoading] = useState(false);
  const [pickerVisible, setPickerVisible] = useState(false);
  const [referenceOptions, setReferenceOptions] = useState([]);

  const resolvedBuildPrompt = buildPrompt || onBuildPrompt;

  const showFeedback = (feedback) => {
    if (onFeedback) {
      onFeedback(feedback);
      return;
    }
    Toast.show(feedback);
  };

  const runCopy = async (selectedReference) => {
    if (!resolvedBuildPrompt) return;

    setLoading(true);
    try {
      const result = await resolvedBuildPrompt(selectedReference);
      const { text, attachmentUrls } = normalizePromptResult(result);
      const copyResult = await copyCoverAiAssistantToClipboard({ text, attachmentUrls });

      showFeedback({
        type: copyResult.copied ? 'success' : 'error',
        text1: buildCoverAiCopyToastMessage(copyResult),
      });
    } catch (error) {
      console.error('Erro ao copiar prompt de capa:', error);
      showFeedback({
        type: 'error',
        text1: 'Não foi possível copiar. Tente novamente no navegador.',
      });
    } finally {
      setLoading(false);
      setPickerVisible(false);
    }
  };

  const handlePress = async (event) => {
    event?.stopPropagation?.();
    if (disabled || loading || !resolvedBuildPrompt) return;

    const options = getReferenceOptions?.() || [];
    if (options.length > 0) {
      setReferenceOptions(options);
      setPickerVisible(true);
      return;
    }

    await runCopy(null);
  };

  const isOverlay = variant === 'overlay';

  return (
    <>
      <CoverAiReferencePickerModal
        visible={pickerVisible}
        title={pickerTitle}
        subtitle={pickerSubtitle}
        options={referenceOptions}
        isLoading={loading}
        onDismiss={() => {
          if (!loading) setPickerVisible(false);
        }}
        onConfirm={runCopy}
      />

      <Pressable
        onPress={handlePress}
        disabled={disabled || loading}
        style={({ hovered }) => [
          styles.button,
          isOverlay ? styles.buttonOverlay : styles.buttonDefault,
          hovered && (isOverlay ? styles.buttonOverlayHovered : styles.buttonDefaultHovered),
          (disabled || loading) && styles.buttonDisabled,
        ]}
      >
        <MaterialIcons
          name="auto-awesome"
          size={14}
          color={isOverlay ? '#FFFFFF' : ADMIN_COLORS.accent}
        />
        <Text style={[styles.label, isOverlay ? styles.labelOverlay : styles.labelDefault]}>
          {loading ? 'Copiando…' : label}
        </Text>
      </Pressable>
    </>
  );
}

const styles = StyleSheet.create({
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    borderRadius: 10,
    paddingVertical: 8,
    paddingHorizontal: 10,
    cursor: 'pointer',
  },
  buttonDefault: {
    borderWidth: 1,
    borderColor: ADMIN_COLORS.border,
    backgroundColor: ADMIN_COLORS.surface,
  },
  buttonDefaultHovered: {
    backgroundColor: ADMIN_COLORS.surfaceSubtle,
  },
  buttonOverlay: {
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.35)',
    backgroundColor: 'rgba(0,0,0,0.45)',
  },
  buttonOverlayHovered: {
    backgroundColor: 'rgba(0,0,0,0.65)',
  },
  buttonDisabled: {
    opacity: 0.6,
  },
  label: {
    fontFamily: ADMIN_FONT,
    fontSize: 12,
    fontWeight: '600',
  },
  labelDefault: {
    color: ADMIN_COLORS.accent,
  },
  labelOverlay: {
    color: '#FFFFFF',
  },
});
